// TODO: remove the "_" import
import _, { chain, flatten, isArray, isEmpty, isObject, isString, map, uniq } from 'lodash';
import { StringUtils } from './StringUtils';
import {
    EntryPoint,
    LocationUserSubscription,
    LocationProductType,
    FieldType,
    LocationHotelsSuppliers,
    LocationType,
    GeoType,
} from '../models';
import { Config } from './Config';
import { ABUtils } from './ABUtils';
import { WMPUtils } from './WMPUtils';
import { CAMPAIGNS } from '../widgets/HotelManager/Campaigns';

export const LocationUtils = {
    filterSuggestedLocations({
        fetchedLocations,
        displayedWord,
        maxAutocompleteSuggestions = 1,
        locationProductType = LocationProductType.FLIGHT,
    }) {
        if (!fetchedLocations) {
            return [];
        }
        _.mixin({
            even: (list) => {
                let parsedList = list;
                if (isObject(list)) {
                    parsedList = flatten(map(isArray(list) ? flatten(list) : list, _.even));
                }
                return parsedList;
            },
        });
        const omitted = ['type', 'geoNodeId', 'relatedLocations'];
        const flightLocations =
            locationProductType === LocationProductType.HOTEL
                ? fetchedLocations.filter((location) => location.type !== LocationType.HOTEL)
                : fetchedLocations;

        let filteredLocations = flightLocations.filter((location) => {
            return chain(location)
                .omit(omitted)
                .values()
                .even()
                .some((value) => {
                    if (!isString(value)) {
                        return false;
                    }
                    return this.locationContains(value.toLowerCase(), displayedWord.toLowerCase());
                }, this)
                .value();
        });

        if (locationProductType === LocationProductType.HOTEL) {
            const hotelLocations =
                locationProductType === LocationProductType.HOTEL
                    ? fetchedLocations.filter((location) => location.type === LocationType.HOTEL)
                    : [];
            if (filteredLocations.length >= maxAutocompleteSuggestions) {
                filteredLocations = filteredLocations.slice(0, maxAutocompleteSuggestions - 1);
            }
            const hotelSuggestionsDisplayed = maxAutocompleteSuggestions - filteredLocations.length;
            filteredLocations = filteredLocations.concat(
                hotelLocations.slice(0, hotelSuggestionsDisplayed),
            );
        } else if (locationProductType === LocationProductType.FLIGHT || locationProductType === LocationProductType.TRAIN) {
            filteredLocations = uniq(filteredLocations, (location) => {
                return map(location.relatedLocations, 'geoNodeId')
                    .sort()
                    .toString();
            });
        }
        return filteredLocations.slice(0, maxAutocompleteSuggestions);
    },

    filterSuggestedLocationsSearchByRegion({
        fetchedLocations,
        maxAutocompleteSuggestions = 1,
    }) {
        if (!fetchedLocations) {
            return [];
        }
        _.mixin({
            even: (list) => {
                let parsedList = list;
                if (isObject(list)) {
                    parsedList = flatten(map(isArray(list) ? flatten(list) : list, _.even));
                }
                return parsedList;
            },
        });

        const filteredLocations = fetchedLocations.filter((location) => {
            return chain(location)
                .values()
                .even()
                .some((value) => {
                    return !(!isString(value));
                }, this)
                .value();
        });

        let [hotelLocations, restLocations] = filteredLocations.reduce(
            ([hotels, rest], suggestion) =>
                suggestion.type === GeoType.ACCOMMODATION
                    ? [[...hotels, suggestion], rest]
                    : [hotels, [...rest, suggestion]],
            [[], []],
        );

        if (restLocations.length >= maxAutocompleteSuggestions && maxAutocompleteSuggestions > 1) {
            restLocations = restLocations.slice(0, maxAutocompleteSuggestions - 1);
        }
        const hotelSuggestionsDisplayed = maxAutocompleteSuggestions - restLocations.length;
        restLocations = restLocations.concat(hotelLocations.slice(0, hotelSuggestionsDisplayed));

        return restLocations.slice(0, maxAutocompleteSuggestions);
    },

    ignoreSpaces(token, minCharsWithoutSpaces) {
        let tokenMinChars = 0;
        let tokenWithSpaces = '';
        for (let i = 0; i < token.length && tokenMinChars < minCharsWithoutSpaces; i++) {
            const c = token[i];
            tokenWithSpaces += c;
            if (c !== ' ') {
                tokenMinChars++;
            }
        }
        return tokenWithSpaces;
    },

    getCleanedKey(displayedWord) {
        return StringUtils.replaceNonAlphaNumeric(displayedWord.toLowerCase(), ' ', true);
    },

    getLocationsKey(displayedWord, autocompleteOptions = Config.get('autocompleteOptions')) {
        const tokenCleaned = this.getCleanedKey(displayedWord);
        const isCoherent = this.isCharCoherentWithLanguage(tokenCleaned, autocompleteOptions);
        const minCharsWithoutSpaces = isCoherent
            ? autocompleteOptions.autocompleteMinChars
            : autocompleteOptions.defaultMinChars;
        return this.ignoreSpaces(tokenCleaned.trim(), minCharsWithoutSpaces);
    },

    isCharCoherentWithLanguage(word, autocompleteOptions) {
        const coherentCharsRegex = new RegExp(autocompleteOptions.coherentCharsRegex, 'g');
        const matchingChars = word.match(coherentCharsRegex);
        return matchingChars !== null && matchingChars.length === word.length;
    },

    isKeywordLongEnoughAccordingWithLanguage(displayedWord, autocompleteOptions, activeProduct) {
        const minChars = this.getMinCharsForAutocomplete(displayedWord, autocompleteOptions, activeProduct);
        const token = StringUtils.replaceNonAlphaNumeric(displayedWord.trim(), '', false);
        return token.length >= minChars;
    },

    getMinCharsForAutocomplete(displayedWord, autocompleteOptions, activeProduct) {
        if (activeProduct === EntryPoint.HOTELS) {
            return autocompleteOptions.accommodationAutocompleteMinChars;
        }
        return this.isCharCoherentWithLanguage(displayedWord, autocompleteOptions)
            ? autocompleteOptions.autocompleteMinChars
            : autocompleteOptions.defaultMinChars;
    },

    getLocationProductType(departureOrArrival, activeProduct) {
        return activeProduct === EntryPoint.HOTELS
            ? LocationProductType.HOTEL
            : departureOrArrival === 'ARRIVAL' && activeProduct === EntryPoint.DP
                ? LocationProductType.DP
                : activeProduct === EntryPoint.TRAINS ? LocationProductType.TRAIN : LocationProductType.FLIGHT;
    },

    getFetchedLocations(
        fetchedLocations,
        locationProductType,
        departureOrArrival,
        activeProduct,
        displayedWord,
        subscription,
        autocompleteOptions = Config.get('autocompleteOptions'),
    ) {
        const isHotelEntryPoint = activeProduct === EntryPoint.HOTELS;
        const key = isHotelEntryPoint
            ? this.getCleanedKey(displayedWord).trim()
            : this.getLocationsKey(displayedWord, autocompleteOptions);
        const locations = isHotelEntryPoint
            ? fetchedLocations[LocationProductType.HOTEL][subscription]
            : fetchedLocations[locationProductType][departureOrArrival];
        return locations.locations[key];
    },

    getFirstSuggestedLocation({
        fetchedLocations,
        displayedWord,
        departureOrArrival,
        activeProduct,
    }) {
        let suggestion = null;
        const locationProductType = (activeProduct === EntryPoint.HOTELS) ? null : this.getLocationProductType(departureOrArrival, activeProduct);
        const subscription = this.getLocationSubscription();
        const fetchedLocation = this.getFetchedLocations(
            fetchedLocations,
            locationProductType,
            departureOrArrival,
            activeProduct,
            displayedWord,
            subscription,
        );
        const locations = fetchedLocation?.locationAutocomplete;
        const filterParams = {
            fetchedLocations: locations,
            displayedWord,
            locationProductType,
        };
        const suggestionValue = activeProduct === EntryPoint.HOTELS ?
            LocationUtils.filterSuggestedLocationsSearchByRegion(filterParams) : LocationUtils.filterSuggestedLocations(filterParams);
        if (!isEmpty(suggestionValue)) {
            const firstSuggestion = suggestionValue[0];
            if (
                (activeProduct === EntryPoint.FLIGHTS ||
                    (activeProduct === EntryPoint.DP &&
                        departureOrArrival === FieldType.DEPARTURE)) &&
                !firstSuggestion.iata &&
                firstSuggestion.relatedLocations?.length > 0
            ) {
                suggestion = firstSuggestion.relatedLocations[0];
            } else {
                suggestion = firstSuggestion;
            }
        }
        return suggestion;
    },

    locationContains(location, searchWord) {
        const replacedSearchWord = StringUtils.replaceNonAlphaNumeric(searchWord, '', true);
        return (
            StringUtils.replaceNonAlphaNumeric(location, '', true).indexOf(replacedSearchWord) >=
                0 && replacedSearchWord.length
        );
    },

    getFieldText(location, direction, activeProduct = null) {
        if (!location) return '';
        const { country, city, iata, caption } = location;
        if (activeProduct === EntryPoint.HOTELS) {
            return this.getInputText(location, activeProduct);
        }
        if (direction === 'rtl') {
            return `${country} ,${city}${iata ? ` - ${iata}` : ''}`;
        }
        return city && country
            ? `${iata ? `${iata} - ` : ''}${city}, ${country}`
            : `${caption ?? ''}`;
    },

    getFieldTextForHotel(location, direction) {
        if (!location) return '';
        const { iata, name, description } = location;
        if (direction === 'rtl') {
            return `${name} ,${description}${iata ? ` - ${iata}` : ''}`;
        }
        return `${iata ? `${iata} - ` : ''} ${name}${description ? `, ${description}` : ''}`;
    },

    getInputText(location, activeProduct = null) {
        if (!location) return '';
        const { displayName, city, country, name, region } = location;
        const locationName = displayName || city || name;
        if (activeProduct === EntryPoint.HOTELS) {
            return `${locationName}${region ? `, ${region}` : ''}${city ? `, ${city}` : ''}${country ? `, ${country}` : ''}`;
        }
        return locationName;
    },

    getLocationSubscription() {
        return Config.get('isPrimeCookieActive')
            ? LocationUserSubscription.PRIME
            : LocationUserSubscription.NON_PRIME;
    },

    isPrimeSubscription(subscription) {
        return subscription === LocationUserSubscription.PRIME;
    },

    getHotelLocationSupplier({ subscription } = {}) {
        const isCampaignActive =
            CAMPAIGNS.includes(WMPUtils.getCampaignId()) &&
            WMPUtils.isCampaignNotExpired();

        const isPrime = this.isPrimeSubscription(subscription);

        if (
            ABUtils.isHsaDesktopActive({ isPrime }) ||
            ABUtils.isHsaMobileActive({ isPrime }) ||
            isCampaignActive
        ) {
            return LocationHotelsSuppliers.HSA;
        }

        return LocationHotelsSuppliers.BOOKING;
    },
};
